import { StoryPeerType } from '@weblite/ts-action/story'
import { IconButton, SxProps, Typography, styled } from '@mui/material'
import {
  DeleteIcon,
  MoreHorizIcon,
  VolumeOffOutlinedIcon,
  VolumeUpIcon,
} from '@weblite/icons'
import { DateString } from '@weblite/ts-action'
import { useCallback, useState } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { HallAvatar, UserAvatar } from '$common/avatar'
import { timeSince } from '$helper/functions/date'
import { useClientUser } from '$logic/client-user'
import { useHall } from '$logic/hall/react-hook'
import { openRoom } from '$logic/opened-room'
import { getPvByUserId } from '$logic/pv'
import { getUserDisplayName, useUser } from '$logic/users'
import { t } from '$i18n'
import {
  ContextMenuItem,
  ContextMenuList,
} from '$comp/_app/components/chat/components/body/components/context-menu/components'
import { StoryAnalyticsApi } from '$logic/story/analytics'

interface SenderInfoActionsProps {
  isVideo?: boolean
  onToggleMuted?: () => void
  isMuted?: boolean
  onDelete?: () => Promise<void>
  canDelete: boolean
  onOpenModal: () => void
  onCloseModal: () => void
}
interface SenderInfoProps extends SenderInfoActionsProps {
  peerId: string
  updatedAt?: DateString
  canDelete: boolean
  loading: boolean
  peerType: StoryPeerType
}

export function UserPeerInfo({
  peerId: userId,
  updatedAt,
  isMuted: isMute,
  isVideo,
  onToggleMuted,
  onDelete,
  canDelete,
  loading,
  onOpenModal,
  onCloseModal,
  peerType,
}: SenderInfoProps) {
  const { user } = useUser(userId)
  const clientUser = useClientUser()
  const isClientUser = clientUser.id === user?.id

  const handleOpenRoom = useCallback(async () => {
    const pv = await getPvByUserId(userId)
    StoryAnalyticsApi.profileClick(userId, peerType)
    if (pv) {
      await closeCurrentModal()
      openRoom(pv.roomId)
    }
  }, [userId, peerType])

  return (
    <Root>
      <UserAvatar
        sx={peerInfoAvatarSx}
        userId={userId}
        onClick={handleOpenRoom}
      />
      <PeerInfoContent
        onClick={handleOpenRoom}
        name={isClientUser ? t('story.yourStory') : getUserDisplayName(user)}
        updatedAt={updatedAt}
      />
      <PeerInfoActions
        isMuted={isMute}
        onToggleMuted={onToggleMuted}
        isVideo={isVideo}
        onDelete={onDelete}
        canDelete={canDelete}
        onOpenModal={onOpenModal}
        onCloseModal={onCloseModal}
      />
    </Root>
  )
}

export function HallPeerInfo({
  peerId: hallId,
  updatedAt,
  onToggleMuted,
  isMuted: isMute,
  isVideo,
  onDelete,
  canDelete,
  loading,
  onOpenModal,
  onCloseModal,
  peerType,
}: SenderInfoProps) {
  const { entity: hall } = useHall(hallId)

  const handleOpenRoom = useCallback(async () => {
    StoryAnalyticsApi.profileClick(hallId, peerType)
    await closeCurrentModal()
    openRoom(hallId)
  }, [hallId, peerType])

  return (
    <Root>
      <HallAvatar
        sx={peerInfoAvatarSx}
        roomId={hallId}
        onClick={handleOpenRoom}
      />
      <PeerInfoContent
        onClick={handleOpenRoom}
        name={hall?.name || ''}
        updatedAt={updatedAt}
      />
      <PeerInfoActions
        isMuted={isMute}
        onToggleMuted={onToggleMuted}
        isVideo={isVideo}
        onDelete={onDelete}
        canDelete={canDelete}
        onOpenModal={onOpenModal}
        onCloseModal={onCloseModal}
      />
    </Root>
  )
}

const PeerInfoContent = ({
  name,
  updatedAt,
  onClick,
}: {
  name: string
  updatedAt?: DateString
  onClick: () => unknown
}) => {
  return (
    <PeerInfoContentRoot>
      <Typography
        onClick={onClick}
        variant="body2"
        color="neutral000"
        maxWidth="100%"
        noWrap
        component="span"
        sx={{ cursor: 'pointer', width: 'fit-content' }}
      >
        {name}
      </Typography>
      {updatedAt && (
        <Typography
          sx={{ width: 'fit-content' }}
          variant="caption1"
          color="neutral000"
        >
          {timeSince(new Date(updatedAt))}
        </Typography>
      )}
    </PeerInfoContentRoot>
  )
}

const PeerInfoActions = ({
  isVideo,
  onToggleMuted,
  isMuted,
  onDelete,
  canDelete,
  onOpenModal,
  onCloseModal,
}: SenderInfoActionsProps) => {
  const [xAxis, setXAxis] = useState<number>(0)
  const [yAxis, setYAxis] = useState<number>(0)
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false)
  return (
    <PeerInfoActionsRoot>
      <ContextMenuList
        open={menuIsOpen}
        xAxis={xAxis}
        yAxis={yAxis}
        disableLoading
        onClose={() => {
          onCloseModal()
          setMenuIsOpen(false)
        }}
      >
        <ContextMenuItem
          text={t('story.viewStoryModal.deleteStory')}
          onClick={() => {
            onDelete?.()
            onCloseModal()
            setMenuIsOpen(false)
          }}
          error
        >
          <DeleteIcon sx={{ color: 'error.main' }} />
        </ContextMenuItem>
      </ContextMenuList>
      {isVideo && (
        <IconButton size="large">
          {isMuted ? (
            <VolumeOffOutlinedIcon
              onClick={onToggleMuted}
              sx={{ color: 'neutral000', width: '24px', height: '24px' }}
            />
          ) : (
            <VolumeUpIcon
              onClick={onToggleMuted}
              sx={{ color: 'neutral000', width: '24px', height: '24px' }}
            />
          )}
        </IconButton>
      )}
      {canDelete && (
        <IconButton
          size="large"
          onClick={e => {
            setMenuIsOpen(true)
            setYAxis(e.clientY)
            setXAxis(e.clientX)
            onOpenModal()
          }}
          data-test-id="peer-story-viewer-more-button"
        >
          <MoreHorizIcon
            sx={{ color: 'neutral000', width: '24px', height: '24px' }}
          />
        </IconButton>
      )}
    </PeerInfoActionsRoot>
  )
}

const Root = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
`

const peerInfoAvatarSx: SxProps = {
  flexShrink: 0,
}

const PeerInfoContentRoot = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
  overflow: hidden;
`

const PeerInfoActionsRoot = styled('div')`
  flex-shrink: 0;
  display: flex;
  gap: 4px;
`
